import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { IEventTicket } from '../../../../../../../models/Api';
import { styleSheet } from '../styles';

interface TicketSelectorProps {
	tickets: IEventTicket[];
	selections: Record<string, number>;
	onSelectionChange: (ticketId: string, quantity: number) => void;
	minQuantities?: Record<string, number>; // Minimum quantities (paid tickets that can't be reduced)
	disabled?: boolean;
}

export const TicketSelector: React.FC<TicketSelectorProps> = ({
	tickets,
	selections,
	onSelectionChange,
	minQuantities = {},
	disabled = false,
}) => {
	/**
	 * Calculate the remaining quantity for a ticket
	 * Returns the minimum of maxAllowed and remaining (whichever is more restrictive)
	 * Returns undefined if no limit is set
	 */
	const getRemainingQuantity = (ticket: IEventTicket): number | undefined => {
		const maxAllowed = ticket.maxAllowed;
		const remaining = ticket.remaining;

		// If maxAlowed isn't defined it's unlimited
		if (maxAllowed === undefined) {
			return undefined;
		}

		// Both defined, use the smaller one
		return Math.min(maxAllowed, remaining);
	};

	/**
	 * Handle increasing ticket quantity
	 * For update scenarios, minQuantities represents already-paid tickets that cannot be reduced
	 * We only validate NEW tickets (above minQuantity) against the remaining limit
	 *
	 * @param ticketId - The ticket ID to increase quantity for
	 */
	const handleIncrease = (ticketId: string) => {
		// Already-paid tickets that user must keep (0 for new registrations)
		const previousQuantity = minQuantities[ticketId || ''] || 0;
		// Current total quantity (including paid + new selections)
		const newTotalQuantity = selections[ticketId] || 0;
		// Only count NEW tickets against the remaining limit
		const newTicketCountToPayFor = newTotalQuantity - previousQuantity;
		const ticket = tickets.find(t => t.id === ticketId);
		const remainingTicketQuantity = ticket ? getRemainingQuantity(ticket) : undefined;

		// Show validation message if trying to exceed limit
		if (remainingTicketQuantity !== undefined && newTicketCountToPayFor > remainingTicketQuantity) {
			const ticketName = ticket?.name || 'This ticket';
			toast.error(`${ticketName} has reached its maximum limit of ${remainingTicketQuantity} tickets`);
			return;
		}

		onSelectionChange(ticketId, newTotalQuantity + 1);
	};

	const handleDecrease = (ticketId: string) => {
		const currentQuantity = selections[ticketId] || 0;
		const minQuantity = minQuantities[ticketId] || 0;
		// Can only decrease if above minimum (paid) quantity
		if (currentQuantity > minQuantity) {
			onSelectionChange(ticketId, currentQuantity - 1);
		}
	};

	const handleQuantityChange = (ticketId: string, value: string) => {
		// Remove any non-digit characters
		const sanitized = value.replace(/[^\d]/g, '');

		const minQuantity = minQuantities[ticketId] || 0;
		const ticket = tickets.find(t => t.id === ticketId);
		const maxQuantity = ticket ? getRemainingQuantity(ticket) : undefined;

		if (sanitized === '') {
			// Can't set below minimum
			onSelectionChange(ticketId, minQuantity);
			return;
		}

		const quantity = parseInt(sanitized, 10);

		// Enforce both minimum and maximum limits
		if (!isNaN(quantity) && quantity >= minQuantity) {
			// Show validation message if exceeding limit
			if (maxQuantity !== undefined && quantity > maxQuantity) {
				const ticketName = ticket?.name || 'This ticket';
				toast.error(`${ticketName} has a maximum limit of ${maxQuantity} tickets`);
				onSelectionChange(ticketId, maxQuantity);
				return;
			}
			onSelectionChange(ticketId, quantity);
		}
	};

	const formatPrice = (priceInCents: number): string => {
		return `$${(priceInCents / 100).toFixed(2)}`;
	};

	// Sort tickets by price (least expensive to most expensive) and filter out hidden tickets
	const sortedTickets = React.useMemo(() => {
		return [...tickets].filter(ticket => !ticket.hideTicket).sort((a, b) => (a.unitPrice || 0) - (b.unitPrice || 0));
	}, [tickets]);

	return (
		<div className={css(styleSheet.ticketSelectorContainer)}>
			<h3 className={css(styleSheet.ticketSelectorHeading)}>Select Your Tickets</h3>
			<div className={css(styleSheet.ticketCards)}>
				{sortedTickets.map(ticket => {
					const quantity = selections[ticket.id || ''] || 0;
					const minQuantity = minQuantities[ticket.id || ''] || 0;
					const remainingQuantity = getRemainingQuantity(ticket);
					const isSoldOut = ticket.showAsSoldOut || (remainingQuantity !== undefined && remainingQuantity === 0);
					// Check if user has reached maximum additional tickets they can purchase
					// For updates: quantity includes already-paid tickets (minQuantity) + new selections
					// Only the NEW selections (quantity - minQuantity) count against the remaining limit
					// Example: If 2 tickets already paid (minQuantity=2), quantity=5, remaining=3
					// Then 5 - 2 = 3 (new tickets) >= 3 (remaining) → at max
					const isAtMaxQuantity = remainingQuantity !== undefined && quantity - minQuantity >= remainingQuantity;

					return (
						<div key={ticket.id} className={css(styleSheet.ticketCard, isSoldOut && styleSheet.ticketCardDisabled)}>
							<div className={css(styleSheet.ticketCardLeft)}>
								<div className={css(styleSheet.ticketHeaderRow)}>
									<h4 className={css(styleSheet.ticketName)}>{ticket.name}</h4>
									{isSoldOut && <span className={css(styleSheet.soldOutBadge)}>SOLD OUT</span>}
								</div>
								<div className={css(styleSheet.ticketPriceRow)}>
									<span className={css(styleSheet.priceAmount)}>{formatPrice(ticket.unitPrice || 0)}</span>
									<span className={css(styleSheet.taxNote)}>Taxes are calculated at checkout</span>
								</div>
								<p className={css(styleSheet.ticketDescription)}>{ticket.description}</p>
							</div>
							<div className={css(styleSheet.ticketCardRight)}>
								<button
									type='button'
									className={css(styleSheet.quantityButton)}
									onClick={() => handleDecrease(ticket.id || '')}
									disabled={disabled || quantity <= minQuantity}
									aria-label='Decrease quantity'
								>
									−
								</button>
								<input
									type='text'
									inputMode='numeric'
									className={css(styleSheet.quantityDisplay)}
									value={quantity}
									onChange={e => handleQuantityChange(ticket.id || '', e.target.value)}
									disabled={disabled}
									aria-label='Ticket quantity'
								/>
								<button
									type='button'
									className={css(styleSheet.quantityButton)}
									onClick={() => handleIncrease(ticket.id || '')}
									disabled={disabled || isSoldOut || isAtMaxQuantity}
									aria-label='Increase quantity'
								>
									+
								</button>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
